import { InternationalAddress } from '../models/international-address.model';

/**
 * Render a structured address as a multi-line block (one part per line), optionally led
 * by a recipient/company name. This is the inverse of how the old free-text address
 * strings were typed, so it can stand in wherever a multi-line address string is expected
 * (ZPL ship-to label, packing slip, scanner) without any parsing.
 *
 * Blank parts are dropped, so a legacy / un-migrated (all-empty) address yields ''.
 * Output shape:
 *   name
 *   line1
 *   line2
 *   city, region postal
 *   country
 *
 * Pass `includeCountry = false` to omit the country line. The shipping surfaces (UPS builder,
 * plastics edit/view, packing slip, printed shipment label, scanner) all pass false because the
 * app is domestic-only — US is implied and the country line is just noise. The country still
 * lives in the data (e.g. the UPS API request, which requires a CountryCode).
 */
export function formatAddressLines(address: InternationalAddress | null | undefined, name?: string, includeCountry = true): string {
    const cityRegionPostal = [
        address?.city,
        [address?.region, address?.postalCode].filter(part => !!part).join(' '),
    ].filter(part => !!part).join(', ');

    return [
        name,
        address?.line1,
        address?.line2,
        cityRegionPostal,
        includeCountry ? address?.country : undefined,
    ].filter(part => !!part).join('\n');
}
